import { useState } from 'react';
import { EllipsisVertical } from 'lucide-react';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import useDeleteBankAccount from '@/features/dashboard/paymentConfig/hooks/useDeleteBankAccount.ts';
import useSetDefaultBankAccount from '@/features/dashboard/paymentConfig/hooks/useSetDefaultBankAccount.ts';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import EditBankAccountModal from '@/features/dashboard/paymentConfig/components/EditBankAccountModal.tsx';
import { BankAccountDto } from '@/features/dashboard/paymentConfig/types/BankAccountDto.ts';
import { getCurrencyLabel } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import Chip from '@/components/ui/Chip.tsx';
import {
  SettingsList,
  SettingsListRow,
} from '@/components/ui/SettingsList.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu.tsx';

const maskAccountNumber = (accountNumber: string): string =>
  `****${accountNumber.slice(-4)}`;

const BankAccountsList = ({
  bankAccounts,
}: {
  bankAccounts: BankAccountDto[];
}) => {
  const [editItem, setEditItem] = useState<BankAccountDto | null>(null);
  const deleteMutation = useDeleteBankAccount();
  const setDefaultMutation = useSetDefaultBankAccount();
  const { confirmDeleteWithCallback } = confirmDialogUseHook();

  const handleDelete = (account: BankAccountDto) => {
    confirmDeleteWithCallback({
      title: 'Usuń konto bankowe',
      description: (
        <>
          Czy na pewno chcesz usunąć konto <b>{account.bankName}</b> (
          {account.accountNumber})?
        </>
      ),
      onConfirm: () => deleteMutation.mutateAsync(account.id),
    });
  };

  return (
    <>
      <SettingsList>
        {bankAccounts.map((account) => (
          <SettingsListRow
            key={account.id}
            leading={
              <Chip
                label={getCurrencyLabel(account.currency)}
                tone="neutral"
                size="sm"
                className="shrink-0"
              />
            }
            title={maskAccountNumber(account.accountNumber)}
            badge={
              account.isDefault && (
                <Chip label="Domyślne" tone="accent" fill="soft" size="sm" />
              )
            }
            subtitle={
              <>
                {account.bankName}
                {account.swiftCode && ` · SWIFT ${account.swiftCode}`}
              </>
            }
            actions={
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <ActionButton tone="outline" size="toolbar" iconOnly>
                    <EllipsisVertical />
                  </ActionButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setEditItem(account)}>
                    Edytuj
                  </DropdownMenuItem>
                  {!account.isDefault && (
                    <DropdownMenuItem
                      onClick={() => setDefaultMutation.mutate(account.id)}
                    >
                      Ustaw jako domyślne
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => handleDelete(account)}
                  >
                    Usuń
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />
        ))}
      </SettingsList>

      {editItem && (
        <EditBankAccountModal
          bankAccount={editItem}
          onClose={() => setEditItem(null)}
        />
      )}
    </>
  );
};

export default BankAccountsList;
